import cn from 'clsx';
import {forwardRef, useEffect, useRef, useState} from 'react';
import useVideoStartTime from './hooks/useVideoStartTime';
import useVideoStopTime from './hooks/useVideoStopTime';
import useVideoTimedHandlers from './hooks/useVideoTimedHandlers';
import styles from './Video.module.scss';

const Video = (props, ref) => {
    const {
        autoplay,
        className,
        containerClassName,
        controlsClassName,
        controlAriaLabel,
        isControlsVisible = true,
        loop,
        muted,
        onCanPlay,
        onControlsClickCustom,
        onError,
        onTimeUpdate,
        onLoadedMetadata,
        playsinline,
        thumbnailSrc,
        src,
        onPlay: onPlayFromProps,
        onPause: onPauseFromProps,
        onEnded,
        onWaiting,
        children,
        startTime,
        stopTime,
    } = props;

    const videoRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const [isVideoCanStart, setIsVideoCanStart] = useState(false);
    const [isAnimationFinished, setIsAnimationFinished] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [hasMediaError, setHasMediaError] = useState(false);

    const stopTimeHandlers = useVideoStopTime({videoRef, stopTime});
    const startTimeHandlers = useVideoStartTime({videoRef, startTime, onLoadedMetadata});

    const {handleLoadedMetadata, handleTimeUpdate, handlePlay, handlePause} = useVideoTimedHandlers({
        stopTimeHandlers,
        startTimeHandlers,
        onTimeUpdate,
        onLoadedMetadata,
    });

    const onCanPlayFunc = () => {
        onCanPlay?.();
        setIsVideoCanStart(true);
    };

    const onTransitionEnd = () => {
        setIsAnimationFinished(true);
    };

    // Show video area as soon as we have src (like App repo's server.get().finally), so video can load and play
    useEffect(() => {
        if (src) setIsLoaded(true);
    }, [src]);

    useEffect(() => {
        if (isAnimationFinished && videoRef.current) {
            if (autoplay) {
                videoRef.current.play?.();
            }
            onCanPlayFunc();
        }
    }, [isAnimationFinished]);

    const onControlButtonClick = () => {
        const video = videoRef.current;

        if (onControlsClickCustom) {
            onControlsClickCustom();
        } else {
            video.paused ? video.play() : video.pause();
        }
    };

    const onPlay = () => {
        setIsVideoPlaying(true);
        handlePlay();
        onPlayFromProps?.();
    };
    const onPause = () => {
        setIsVideoPlaying(false);
        handlePause();
        onPauseFromProps?.();
    };

    const handleTimeUpdateInternal = (event) => {
        handleTimeUpdate(event);
    };

    const handleLoadedMetadataInternal = (event) => {
        handleLoadedMetadata(event);
    };

    const handleMediaError = (event) => {
        setHasMediaError(true);
        onError?.(event);
    };

    return (
        <div className={cn(styles.VideoContainer, containerClassName)} aria-hidden='true'>
            {isLoaded && !hasMediaError && (
                <>
                    {children}
                    <video
                        autoPlay={autoplay}
                        className={className}
                        loop={loop}
                        onEnded={onEnded}
                        muted={muted}
                        onCanPlay={onCanPlayFunc}
                        onTimeUpdate={handleTimeUpdateInternal}
                        onPause={onPause}
                        onPlay={onPlay}
                        playsInline={playsinline}
                        onLoadedMetadata={handleLoadedMetadataInternal}
                        ref={(node) => {
                            videoRef.current = node;
                            if (typeof ref === 'function') ref(node);
                            else if (ref) ref.current = node;
                        }}
                        src={src}
                        onWaiting={onWaiting}
                        onError={handleMediaError}
                    />
                    {isControlsVisible && (
                        <button
                            type='button'
                            className={cn(
                                'absolute right-4 bottom-4 z-2 hidden h-11 w-11 cursor-pointer items-center justify-center rounded-full border-2 border-primary-dark border-solid bg-white p-0 md:right-8',
                                controlsClassName,
                                {
                                    flex: isLoaded,
                                }
                            )}
                            onClick={onControlButtonClick}
                            tabIndex={-1}
                            aria-label={controlAriaLabel}
                        >
                            <div
                                className={cn(styles.ControlButton, {
                                    [styles.Paused]: !isVideoPlaying,
                                })}
                            />
                        </button>
                    )}
                </>
            )}

            <div aria-hidden='true'>
                {thumbnailSrc && !hasMediaError && (
                    <img
                        onTransitionEnd={onTransitionEnd}
                        className={cn(styles.Thumbnail, {'opacity-0': isVideoCanStart}, className)}
                        src={thumbnailSrc}
                        alt=''
                        onError={handleMediaError}
                    />
                )}
            </div>
        </div>
    );
};

export default forwardRef(Video);
